import { useCashFlowPlanner } from "@/hooks/useCashFlowPlanner";
import { StateMessage } from "@/components/StateMessage";
import { formatMoney } from "@/lib/format";
import "./CashFlowPlannerCard.css";

/** `CashFlowEvent.kind` -> the word this card shows for it. */
const KIND_LABEL: Record<string, string> = {
  payroll: "Payroll",
  bill: "Bill due",
  invoice: "Invoice due",
};

/**
 * Home's read-only 8-week (default) cash-flow projection — ef-048-5. Every figure is the
 * API's own (`GET /api/demo/home/cash-flow`, `useCashFlowPlanner`); nothing here computes
 * or invents a number, same discipline as every other card on this dashboard (see
 * Dashboard.tsx's own `DEMO_KPIS` remarks for why that matters here specifically).
 *
 * `role="region"` + `aria-label` (not `aria-labelledby`) and `data-state` on the SAME
 * element — the build brief's own requirement: both the dotnet-independent E2E spec
 * (`home-cash-flow.spec.ts`, API-stubbed via `page.route`) and `rules/ui-breadcrumbs.md`
 * §1's role-based-locator rule read this element directly, not a descendant.
 *
 * NOT `CashFlowChart` (a different, backward-looking fixture — that component's own doc
 * explains why it stays a static seven-month illustration). This card is the forward-
 * looking one: real upcoming paydays and real outstanding bills/invoices, never a demo
 * shape.
 */
export function CashFlowPlannerCard() {
  const { planner, status, error } = useCashFlowPlanner();
  const isEmpty = status === "ready" && (planner?.events.length ?? 0) === 0;
  const dataState = isEmpty ? "empty" : status;

  return (
    <section
      className="cashflow-planner"
      role="region"
      aria-label="Cash flow planner"
      data-state={dataState}
    >
      <div className="cashflow-planner__head">
        <h2 className="cashflow-planner__title">Cash flow</h2>
        {planner && (
          <span className="cashflow-planner__horizon">Next {planner.weeks} weeks</span>
        )}
      </div>

      {status === "loading" && <StateMessage tone="loading" title="Loading cash flow…" />}

      {status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load the cash-flow projection."
          detail={error ?? undefined}
        />
      )}

      {planner && (
        <>
          <p className="cashflow-planner__current">
            {/* "Starting cash", never "Cash on hand" — that vocabulary belongs to the
                dashboard's own hero KPI (ADR 0009 §4, one vocabulary per concept).
                dashboard.spec.ts's "populated activity feed" case locates the hero via
                getByText(/cash on hand/i) in strict mode; a second element repeating it
                broke that locator (Gate 45). This card's own figure is the SAME number
                (currentCash), just the planner's own starting point for the projection
                below it. */}
            Starting cash
            <span className="cashflow-planner__current-amount">
              {formatMoney(planner.currentCash)}
            </span>
          </p>

          {isEmpty ? (
            <StateMessage
              tone="empty"
              title="Nothing scheduled in this window."
              detail="No upcoming paydays or outstanding bills/invoices land in the next
                weeks — an honest empty state, not a loading spinner."
            />
          ) : (
            <ul className="cashflow-planner__list">
              {planner.events.map((event, index) => (
                <li
                  // date+kind+label is unique per event within one tenant's projection —
                  // no server-issued id exists for a synthetic projected row.
                  key={`${event.date}-${event.kind}-${event.label}-${index}`}
                  className="cashflow-planner__row"
                >
                  <span className="cashflow-planner__date">{event.date}</span>
                  <span className="cashflow-planner__kind">
                    {KIND_LABEL[event.kind] ?? event.kind}
                  </span>
                  <span className="cashflow-planner__label">{event.label}</span>
                  <span
                    className={`cashflow-planner__amount${
                      event.amount < 0 ? " cashflow-planner__amount--out" : " cashflow-planner__amount--in"
                    }`}
                  >
                    {formatMoney(event.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
